import type { CampaignAdProduct } from '@/types/spCampaigns';

export type UnifiedAdGroupRow = {
  adGroupId: string;
  campaignId: string;
  adProduct: CampaignAdProduct;
  name: string;
  state: string;
  servingStatus: string;
  raw: Record<string, unknown>;
};

type SpAdGroupRow = {
  ad_group_id?: string;
  campaign_id?: string;
  name?: string;
  state?: string;
  extended_data?: { serving_status?: string } | null;
};

type SbAdGroupRow = SpAdGroupRow;
type SdAdGroupRow = SpAdGroupRow;

function normalizeAdGroup(
  row: SpAdGroupRow,
  adProduct: CampaignAdProduct
): UnifiedAdGroupRow | null {
  const adGroupId = row.ad_group_id ? String(row.ad_group_id).trim() : '';
  const campaignId = row.campaign_id ? String(row.campaign_id).trim() : '';
  if (!adGroupId || !campaignId) return null;
  return {
    adGroupId,
    campaignId,
    adProduct,
    name: row.name ? String(row.name) : 'Unnamed',
    state: row.state ? String(row.state).toUpperCase() : 'UNKNOWN',
    servingStatus: row.extended_data?.serving_status
      ? String(row.extended_data.serving_status)
      : '—',
    raw: row as Record<string, unknown>,
  };
}

export function mergeAdGroups(
  spRows: SpAdGroupRow[],
  sbRows: SbAdGroupRow[],
  sdRows: SdAdGroupRow[]
): UnifiedAdGroupRow[] {
  const merged: UnifiedAdGroupRow[] = [];
  for (const row of spRows) {
    const normalized = normalizeAdGroup(row, 'SP');
    if (normalized) merged.push(normalized);
  }
  for (const row of sbRows) {
    const normalized = normalizeAdGroup(row, 'SB');
    if (normalized) merged.push(normalized);
  }
  for (const row of sdRows) {
    const normalized = normalizeAdGroup(row, 'SD');
    if (normalized) merged.push(normalized);
  }
  merged.sort((a, b) => {
    const nameCmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    if (nameCmp !== 0) return nameCmp;
    return a.adGroupId.localeCompare(b.adGroupId);
  });
  return merged;
}

export function parseAdGroupsListPayload(data: unknown): {
  adGroups: SpAdGroupRow[];
  nextToken: string | null;
} {
  if (!data || typeof data !== 'object') {
    return { adGroups: [], nextToken: null };
  }
  const raw = data as Record<string, unknown>;
  const adGroups = Array.isArray(raw.ad_groups) ? (raw.ad_groups as SpAdGroupRow[]) : [];
  const nextToken =
    typeof raw.next_token === 'string' && raw.next_token.trim() ? raw.next_token : null;
  return { adGroups, nextToken };
}

export function parseSdAdGroupsPayload(data: unknown): SpAdGroupRow[] {
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  return Array.isArray(raw.ad_groups) ? (raw.ad_groups as SpAdGroupRow[]) : [];
}
